import { reportError } from './report';

/*
 * THE FLIGHT RECORDER FOR ONE STOP (5 Oct).
 *
 * A driver said he finished a stop on site and it came back open at the next
 * one. The server had nothing: no attempt, no refusal, no upload. Whatever
 * happened, happened on the phone, and the phone kept no record of it.
 *
 * So every stop now keeps its own list of steps -- opened, each screen,
 * Complete pressed, saved on the phone, the answer -- with seconds since it
 * was opened, and sends that list as ONE record when the driver leaves the
 * stop. One record per stop, not one per step, so a full route costs a
 * handful of reports, not hundreds.
 *
 * Kept in localStorage as it goes, so a stop the phone killed half way is
 * still sent, as 'cut', the next time the app opens.
 */

const KEY = 'jw.stopLog';
const MAX_STEPS = 30;

interface Log { task: string; opened: number; steps: string[] }

function readAll(): Record<string, Log> {
  try { return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, Log>; }
  catch { return {}; }
}
function writeAll(all: Record<string, Log>): void {
  try { localStorage.setItem(KEY, JSON.stringify(all)); } catch { /* full or private */ }
}

/** Note one step at a stop. Cheap, never throws. */
export function stopStep(taskId: string | undefined, step: string): void {
  if (!taskId) return;
  const all = readAll();
  const now = Date.now();
  const log = all[taskId] ?? { task: taskId, opened: now, steps: [] };
  const at = Math.round((now - log.opened) / 1000);
  log.steps.push(`${step.slice(0, 28)}@${at}`);
  if (log.steps.length > MAX_STEPS) log.steps.splice(1, 1);   // keep 'open'
  all[taskId] = log;
  writeAll(all);
}

function send(log: Log, end: string): void {
  const opened = new Date(log.opened).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const detail = JSON.stringify({
    task: log.task.slice(0, 8), opened, end, steps: log.steps.join(' '),
  });
  reportError('diag_stop', 'stop', detail.slice(0, 480));
}

/** The driver left the stop screen. Sends the stop's record. */
export function stopEnd(taskId: string | undefined, end: string): void {
  if (!taskId) return;
  const all = readAll();
  const log = all[taskId];
  if (!log) return;
  delete all[taskId];
  writeAll(all);
  send(log, end);
}

/** On app start: stops whose screen never closed -- the phone or the driver cut it. */
export function sendCutStops(): void {
  const all = readAll();
  const ids = Object.keys(all);
  if (ids.length === 0) return;
  writeAll({});
  for (const id of ids) {
    const log = all[id];
    if (log) send(log, 'cut');
  }
}
